import React, { useState, useRef, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { scrollbarStyles } from '../../utils/scrollbar'
import Paper from '@material-ui/core/Paper'
import IconButton from '@material-ui/core/IconButton'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import Radio from '@material-ui/core/Radio'
import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import Chip from '@material-ui/core/Chip'
import Button from '@material-ui/core/Button'
import LayersIcon from '@material-ui/icons/Layers'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SearchIcon from '@material-ui/icons/Search'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import ClearAllIcon from '@material-ui/icons/ClearAll'
import L from 'leaflet'
import { useTranslation } from 'react-i18next'
import LayerTooltip from './dialog/LayerToolTip'

const DRAWER_WIDTH = 400
const DEFAULT_GEE_ACCENT = '#232C57'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    zIndex: 1000,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    top: ({ top }) => top,
    right: ({ right }) => right,
  },
  tool: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 36,
    height: 36,
    backgroundColor: '#fff',
    border: '1.5px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    borderRadius: 12,
    color: '#9CA3AF',
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    padding: 0,
    minWidth: 36,
    '&:hover': {
      backgroundColor: '#fff',
      color: '#232C57',
      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
      borderColor: 'rgba(35, 44, 87, 0.25)',
    },
  },
  iconButton: {
    padding: 8,
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  active: {
    backgroundColor: '#232C57',
    color: '#fff',
    borderColor: '#232C57',
    boxShadow: '0 2px 8px rgba(35, 44, 87, 0.4)',
    '&:hover': {
      backgroundColor: '#1a2145',
      color: '#fff',
      borderColor: '#1a2145',
    },
  },
  icon: {
    fontSize: 19,
    width: 19,
    height: 19,
  },
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxWidth: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fafbfc',
  },
  drawerHeader: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    marginTop: theme.spacing(8),
    flexShrink: 0,
    backgroundColor: '#fff',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  closeButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  clearAllButton: {
    fontSize: '0.75rem',
    textTransform: 'none',
    color: theme.palette.grey[500],
    padding: theme.spacing(0.25, 1),
    minWidth: 0,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'rgba(244, 67, 54, 0.04)',
    },
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      height: 36,
      borderRadius: 8,
      backgroundColor: theme.palette.grey[50],
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.08)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.16)',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '0.85rem',
      '&::placeholder': {
        fontSize: '0.85rem',
        opacity: 0.5,
      },
    },
  },
  activeChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: theme.spacing(1, 0, 0, 0),
  },
  activeChip: {
    height: 24,
    fontSize: '0.7rem',
    borderRadius: 12,
    backgroundColor: 'rgba(35, 44, 87, 0.08)',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      fontSize: 14,
      marginRight: 2,
      color: 'rgba(0, 0, 0, 0.3)',
      '&:hover': {
        color: 'rgba(0, 0, 0, 0.6)',
      },
    },
  },
  customScrollbar: {
    ...scrollbarStyles,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.7rem',
    color: theme.palette.grey[400],
    padding: theme.spacing(1.5, 2, 0.5, 2),
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  groupAccordion: {
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
    backgroundColor: 'transparent',
  },
  groupAccordionSummary: {
    minHeight: 36,
    padding: theme.spacing(0, 2),
    '&.Mui-expanded': { minHeight: 36 },
    '& .MuiAccordionSummary-content': {
      margin: theme.spacing(0.5, 0),
      alignItems: 'center',
      '&.Mui-expanded': { margin: theme.spacing(0.5, 0) },
    },
    '& .MuiAccordionSummary-expandIcon': {
      padding: 4,
      color: theme.palette.grey[400],
    },
  },
  groupAccordionDetails: {
    padding: theme.spacing(0, 1, 0.5, 1),
    flexDirection: 'column',
  },
  groupTitle: {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    flex: 1,
  },
  countBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: 10,
    marginLeft: 8,
    lineHeight: 1.5,
  },
  // Layer card styles
  layerCard: {
    margin: theme.spacing(0.5, 0),
    borderRadius: 10,
    backgroundColor: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    '&:hover': {
      border: '1px solid rgba(0, 0, 0, 0.12)',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
    },
  },
  layerCardActive: {
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  },
  layerCardAccent: {
    borderLeft: '4px solid',
  },
  layerItem: {
    padding: theme.spacing(0.75, 1.5),
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  listItemIcon: {
    minWidth: 32,
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.82rem',
      fontWeight: 400,
    },
  },
  listItemTextActive: {
    '& .MuiListItemText-primary': {
      fontSize: '0.82rem',
      fontWeight: 500,
    },
  },
  alertLayerStatus: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(0.5),
  },
  errorIcon: {
    fontSize: 16,
    color: theme.palette.error.main,
  },
  // Active layer expanded content
  layerExpandedContent: {
    padding: theme.spacing(0, 1.5, 1, 1.5),
  },
  opacityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: theme.spacing(0, 0.5),
  },
  opacityLabel: {
    fontSize: '0.7rem',
    color: theme.palette.grey[400],
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    fontWeight: 600,
    minWidth: 50,
  },
  opacitySlider: {
    flex: 1,
    '& .MuiSlider-rail': {
      height: 3,
      borderRadius: 2,
      opacity: 0.2,
    },
    '& .MuiSlider-track': {
      height: 3,
      borderRadius: 2,
    },
    '& .MuiSlider-thumb': {
      width: 14,
      height: 14,
      marginTop: -5.5,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        boxShadow: '0 0 0 6px rgba(35, 44, 87, 0.08)',
      },
    },
  },
  opacityValue: {
    fontSize: '0.7rem',
    color: theme.palette.grey[500],
    fontWeight: 500,
    minWidth: 28,
    textAlign: 'right',
  },
  yearRangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: theme.spacing(0, 0.5),
    marginTop: 4,
  },
  yearRangeLabel: {
    fontSize: '0.7rem',
    color: theme.palette.grey[400],
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    fontWeight: 600,
    minWidth: 36,
  },
  yearRangeValue: {
    fontSize: '0.65rem',
    color: theme.palette.grey[500],
    fontWeight: 500,
    minWidth: 70,
    textAlign: 'right',
  },
  windowPresetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    padding: theme.spacing(0, 0.5),
    marginTop: 6,
  },
  windowPreset: {
    fontSize: '0.62rem',
    padding: '1px 8px',
    minHeight: 20,
    lineHeight: 1.4,
    textTransform: 'none',
    borderRadius: 10,
  },
  dualRangeContainer: {
    position: 'relative',
    flex: 1,
    height: 20,
    '& input[type="range"]': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      background: 'transparent',
      pointerEvents: 'none',
      appearance: 'none',
      '-webkit-appearance': 'none',
      '&::-webkit-slider-thumb': {
        pointerEvents: 'auto',
        appearance: 'none',
        '-webkit-appearance': 'none',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: theme.palette.primary.main,
        border: '2px solid #fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        cursor: 'pointer',
        marginTop: -5,
      },
      '&::-moz-range-thumb': {
        pointerEvents: 'auto',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: theme.palette.primary.main,
        border: '2px solid #fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      },
      '&::-webkit-slider-runnable-track': {
        height: 4,
        background: 'transparent',
      },
      '&::-moz-range-track': {
        height: 4,
        background: 'transparent',
      },
    },
  },
  dualRangeTrack: {
    position: 'absolute',
    top: 8,
    left: 0,
    right: 0,
    height: 4,
    borderRadius: 2,
    background: theme.palette.grey[300],
  },
  dualRangeActiveTrack: {
    position: 'absolute',
    top: 8,
    height: 4,
    borderRadius: 2,
  },
  gradientLegendBar: {
    height: 10,
    borderRadius: 4,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    marginTop: 4,
  },
  gradientLegendLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  gradientLegendLabel: {
    fontSize: '0.6rem',
    color: theme.palette.grey[500],
  },
  // Legend accordion
  legendAccordion: {
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
    marginTop: theme.spacing(0.5),
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px !important',
    border: '1px solid rgba(0, 0, 0, 0.04)',
  },
  legendAccordionSummary: {
    minHeight: 26,
    padding: theme.spacing(0, 1),
    '&.Mui-expanded': { minHeight: 26 },
    '& .MuiAccordionSummary-content': {
      margin: theme.spacing(0.25, 0),
      '&.Mui-expanded': { margin: theme.spacing(0.25, 0) },
    },
    '& .MuiAccordionSummary-expandIcon': {
      padding: 2,
      marginRight: -4,
      color: theme.palette.grey[400],
    },
  },
  legendAccordionDetails: {
    padding: theme.spacing(0, 1, 0.75, 1),
  },
  legendTitle: {
    fontSize: '0.65rem',
    fontWeight: 600,
    color: theme.palette.grey[400],
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  legendImage: {
    maxWidth: '100%',
    display: 'block',
  },
  legendImageList: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  legendImageItem: {
    minWidth: 0,
  },
  legendImageLabel: {
    marginBottom: 4,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '2px 0',
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
    border: '1px solid rgba(0, 0, 0, 0.1)',
  },
  legendLabel: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
}))

// GEE alert layer groups definition
const GEE_ALERT_GROUPS = [
  {
    name: 'GLAD Alerts',
    layers: [
      { name: 'GLAD-L before 2020' },
      { name: 'GLAD-L after 2020' },
      { name: 'GLAD-S2 before 2020' },
      { name: 'GLAD-S2 after 2020' },
    ],
  },
  {
    name: 'Other Alert Systems',
    layers: [
      { name: 'DIST-ALERT' },
      { name: 'RADD before 2020' },
      { name: 'RADD after 2020' },
      { name: 'Integrated Alerts (GFW)' },
    ],
  },
  {
    name: 'Alert Drivers',
    layers: [
      { name: 'Drivers of Deforestation Alerts' },
      { name: 'Tree Cover Loss Drivers (1 km)' },
    ],
  },
  {
    name: 'Fires',
    layers: [
      { name: 'Fire Detections (VIIRS)', windowDays: true },
      { name: 'Tree Cover Loss from Fires' },
    ],
  },
  {
    name: 'Water & ESG Risk',
    layers: [
      { name: 'Water Stress (Aqueduct)' },
      { name: 'ESG Risk (Aqueduct)' },
    ],
  },
]

// Colors extracted from the current Geobosques 2001-2025 service legend.
// The gradient uses the anthropogenic-loss color for 2025; the separate
// natural-loss color is shown alongside the layer in MapLayersManager.
const PERDIDA_YEAR_COLORS = {
  2025: '#FFAA00',
  2024: '#C91E17', 2023: '#CB281D', 2022: '#CE3224', 2021: '#D03C2B',
  2020: '#D24632', 2019: '#D5503A', 2018: '#D75A41', 2017: '#D96449',
  2016: '#DC6D50', 2015: '#DE7758', 2014: '#E18060', 2013: '#E38969',
  2012: '#E59371', 2011: '#E89B79', 2010: '#EAA482', 2009: '#ECAD8B',
  2008: '#EFB594', 2007: '#F1BE9D', 2006: '#F3C6A6', 2005: '#F6CEAF',
  2004: '#F8D6B9', 2003: '#FADDC2', 2002: '#FDE4CC', 2001: '#FFEBD6',
}

// Windows offered for windowed layers (VIIRS fires), in days ending now
// (rolling). No 24 h preset: the Earth Engine LANCE collections hold one
// image per UTC day with about a day of lag, so a 24 h window would show a
// clock-dependent fraction of yesterday. Must stay within the backend cap.
const WINDOW_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]
const DEFAULT_WINDOW_DAYS = 7

function LayerCard({
  name,
  visible,
  accent,
  legendUrl,
  legendUrls,
  legend,
  isLoading,
  isError,
  opacity,
  noOpacity,
  onOpacityChange,
  onToggle,
  yearRange,
  legendGradient,
  onYearRangeChange,
  // Single-year layers: the years to pick from, the selected one and its setter.
  years,
  year,
  onYearChange,
  // Layers with display modes (e.g. land cover 'Default' / 'Detailed'): options, selected, setter.
  modes,
  mode,
  onModeChange,
  // Windowed layers: selected window in days (7 by default) and its setter.
  windowDays,
  onWindowDaysChange,
  classes,
  t,
}) {
  const [localOpacity, setLocalOpacity] = useState(Math.round((opacity ?? 0.6) * 100))
  const showWindowPresets = Boolean(onWindowDaysChange)
  const activeWindowDays = windowDays || DEFAULT_WINDOW_DAYS

  // Sync from parent when not dragging (e.g. when opacity reset externally)
  React.useEffect(() => {
    setLocalOpacity(Math.round((opacity ?? 0.6) * 100))
  }, [opacity])

  const [localYearRange, setLocalYearRange] = useState(yearRange || [2001, 2025])
  React.useEffect(() => {
    if (yearRange) setLocalYearRange(yearRange)
  }, [yearRange])

  return (
    <div
      className={`${classes.layerCard} ${visible ? classes.layerCardActive : ''}`}
      style={visible ? { borderLeftColor: accent, borderLeftWidth: 4, borderLeftStyle: 'solid' } : undefined}
    >
      <ListItem
        button
        onClick={onToggle}
        className={classes.layerItem}
        disabled={isLoading}
      >
        <ListItemIcon className={classes.listItemIcon}>
          {isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <Checkbox
              checked={visible}
              size="small"
              color="primary"
              disabled={isLoading}
            />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span style={{ flex: 1 }}>{t(name)}</span>
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 8 }}>
                <LayerTooltip layer={{ name }} marginLeft={1} />
              </span>
            </span>
          }
          className={visible ? classes.listItemTextActive : classes.listItemText}
        />
        {isError && (
          <ListItemSecondaryAction className={classes.alertLayerStatus}>
            {isError && (
              <ErrorOutlineIcon
                className={classes.errorIcon}
                titleAccess={t('Error loading layer')}
              />
            )}
          </ListItemSecondaryAction>
        )}
      </ListItem>
      {visible && (
        <div className={classes.layerExpandedContent}>
          {!noOpacity && (
            <div className={classes.opacityRow}>
              <span className={classes.opacityLabel}>{t('Opacity')}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={localOpacity}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  setLocalOpacity(val)
                  if (onOpacityChange) onOpacityChange(name, val / 100)
                }}
                style={{ flex: 1, accentColor: accent, cursor: 'pointer' }}
              />
              <span className={classes.opacityValue}>{localOpacity}%</span>
            </div>
          )}
          {showWindowPresets && (
            <div className={classes.windowPresetRow}>
              {WINDOW_PRESETS.map((preset) => {
                const active = activeWindowDays === preset.days
                return (
                  <Button
                    key={preset.days}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    className={classes.windowPreset}
                    style={active ? { backgroundColor: accent, color: '#fff' } : { borderColor: accent, color: accent }}
                    onClick={() => onWindowDaysChange(name, preset.days)}
                  >
                    {t(preset.label)}
                  </Button>
                )
              })}
            </div>
          )}
          {modes && modes.length > 1 && onModeChange && (
            <div className={classes.windowPresetRow}>
              {modes.map((option) => {
                const active = mode === option
                return (
                  <Button
                    key={option}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    className={classes.windowPreset}
                    style={active ? { backgroundColor: accent, color: '#fff' } : { borderColor: accent, color: accent }}
                    onClick={() => onModeChange(name, option)}
                  >
                    {t(option)}
                  </Button>
                )
              })}
            </div>
          )}
          {years && years.length > 1 && onYearChange && (
            <div className={classes.windowPresetRow}>
              {years.map((option) => {
                const active = year === option
                return (
                  <Button
                    key={option}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    className={classes.windowPreset}
                    style={active ? { backgroundColor: accent, color: '#fff' } : { borderColor: accent, color: accent }}
                    onClick={() => onYearChange(name, option)}
                  >
                    {option}
                  </Button>
                )
              })}
            </div>
          )}
          {yearRange && (
            <div className={classes.yearRangeRow}>
              <span className={classes.yearRangeLabel}>{t('Years')}</span>
              <div className={classes.dualRangeContainer}>
                <div className={classes.dualRangeTrack} />
                <div
                  className={classes.dualRangeActiveTrack}
                  style={{
                    left: `${((localYearRange[0] - 2001) / 24) * 100}%`,
                    right: `${((2025 - localYearRange[1]) / 24) * 100}%`,
                    background: accent,
                  }}
                />
                <input
                  type="range"
                  min={2001}
                  max={2025}
                  step={1}
                  value={localYearRange[0]}
                  onChange={(e) => {
                    const v = Math.min(Number(e.target.value), localYearRange[1])
                    const val = [v, localYearRange[1]]
                    setLocalYearRange(val)
                    if (onYearRangeChange) onYearRangeChange(name, val)
                  }}
                  style={{ zIndex: localYearRange[0] > 2012 ? 3 : 1 }}
                />
                <input
                  type="range"
                  min={2001}
                  max={2025}
                  step={1}
                  value={localYearRange[1]}
                  onChange={(e) => {
                    const v = Math.max(Number(e.target.value), localYearRange[0])
                    const val = [localYearRange[0], v]
                    setLocalYearRange(val)
                    if (onYearRangeChange) onYearRangeChange(name, val)
                  }}
                  style={{ zIndex: localYearRange[1] <= 2012 ? 3 : 1 }}
                />
              </div>
              <span className={classes.yearRangeValue}>
                {localYearRange[0]}–{localYearRange[1]}
              </span>
            </div>
          )}
          {(legendUrl || (legendUrls && legendUrls.length > 0)) && (
            <Accordion defaultExpanded className={classes.legendAccordion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ fontSize: 14 }} />}
                className={classes.legendAccordionSummary}
              >
                <Typography className={classes.legendTitle}>{t('Legend')}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.legendAccordionDetails}>
                <div className={classes.legendImageList}>
                  {(legendUrls?.length ? legendUrls : [{ url: legendUrl }]).map((item, i) => (
                    <div key={item.url || i} className={classes.legendImageItem}>
                      {item.label && (
                        <Typography className={classes.legendImageLabel}>{t(item.label)}</Typography>
                      )}
                      <img
                        src={item.url}
                        alt={`${t(item.label || name)} ${t('Legend')}`}
                        className={classes.legendImage}
                      />
                    </div>
                  ))}
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          {legend && legend.length > 0 && (
            <Accordion defaultExpanded className={classes.legendAccordion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ fontSize: 14 }} />}
                className={classes.legendAccordionSummary}
              >
                <Typography className={classes.legendTitle}>{t('Legend')}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.legendAccordionDetails}>
                <div>
                  {legend.map((item, i) => (
                    <div key={i} className={classes.legendRow}>
                      <span className={classes.legendSwatch} style={{ backgroundColor: item.color }} />
                      <span className={classes.legendLabel}>{t(item.label)}</span>
                    </div>
                  ))}
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          {legendGradient && yearRange && (
            <Accordion defaultExpanded className={classes.legendAccordion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ fontSize: 14 }} />}
                className={classes.legendAccordionSummary}
              >
                <Typography className={classes.legendTitle}>{t('Legend')}</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.legendAccordionDetails}>
                <div style={{ width: '100%' }}>
                  <div
                    className={classes.gradientLegendBar}
                    style={{
                      background: (() => {
                        const [startYear, endYear] = localYearRange
                        const span = endYear - startYear || 1
                        const stops = []
                        for (let y = startYear; y <= endYear; y++) {
                          const pct = ((y - startYear) / span) * 100
                          stops.push(`${PERDIDA_YEAR_COLORS[y] || '#ccc'} ${pct}%`)
                        }
                        return `linear-gradient(to right, ${stops.join(', ')})`
                      })(),
                    }}
                  />
                  <div className={classes.gradientLegendLabels}>
                    <span className={classes.gradientLegendLabel}>{localYearRange[0]}</span>
                    <span className={classes.gradientLegendLabel}>{localYearRange[1]}</span>
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </div>
  )
}

export default function LeafletLayersDrawer({
  baseLayers = [],
  overlayGroups = [],
  onBaseLayerChange,
  onOverlayToggle,
  onOverlayOpacityChange,
  onOverlayYearRangeChange,
  onOverlayYearChange,
  onOverlayModeChange,
  onClearAllLayers,
  geeAlertLayers = {},
  geeAlertVisibility = {},
  geeAlertOpacity = {},
  onGeeAlertToggle,
  onGeeAlertOpacityChange,
  geeAlertWindowDays = {},
  onGeeAlertWindowDaysChange,
  noAppBar = false,
  top = 76,
  right = 12,
}) {
  const classes = useStyles({ top, right })
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const paperRef = useRef(null)
  const scrollContainerRef = useRef(null)

  const paperRefCallback = useCallback((node) => {
    paperRef.current = node
    if (node) {
      L.DomEvent.disableClickPropagation(node)
      L.DomEvent.disableScrollPropagation(node)
    }
  }, [])

  const handleOpen = () => {
    setOpen(true)
    // Scroll drawer content to top when opening
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0
    })
  }
  const handleClose = () => setOpen(false)

  const handleBaseLayerSelect = (layerName) => {
    if (onBaseLayerChange) onBaseLayerChange(layerName)
  }

  const handleOverlayToggle = (layerName) => {
    if (onOverlayToggle) {
      let currentLayer = null
      for (const group of overlayGroups) {
        currentLayer = group.overlays.find((o) => o.name === layerName)
        if (currentLayer) break
      }
      onOverlayToggle(layerName, !currentLayer?.visible)
    }
  }

  const handleGeeAlertToggle = (layerName) => {
    if (onGeeAlertToggle) {
      const currentVisible = geeAlertVisibility[layerName] ?? false
      onGeeAlertToggle(layerName, !currentVisible)
    }
  }

  // Collect all active layers for chips
  const activeLayers = []
  overlayGroups.forEach((group) => {
    group.overlays.forEach((overlay) => {
      if (overlay.visible) activeLayers.push({ name: overlay.name, type: 'overlay' })
    })
  })
  GEE_ALERT_GROUPS.forEach((group) => {
    group.layers.forEach((layer) => {
      if (geeAlertVisibility[layer.name]) activeLayers.push({ name: layer.name, type: 'gee' })
    })
  })

  const lowerSearch = searchTerm.toLowerCase()
  const filteredBaseLayers = searchTerm
    ? baseLayers.filter((l) => l.name.toLowerCase().includes(lowerSearch))
    : baseLayers
  const filteredOverlayGroups = searchTerm
    ? overlayGroups
        .map((group) => ({
          ...group,
          overlays: group.overlays.filter((o) =>
            o.name.toLowerCase().includes(lowerSearch)
          ),
        }))
        .filter((group) => group.overlays.length > 0)
    : overlayGroups
  const filteredGeeGroups = searchTerm
    ? GEE_ALERT_GROUPS.map((group) => ({
        ...group,
        layers: group.layers.filter((layer) =>
          layer.name.toLowerCase().includes(lowerSearch)
        ),
      })).filter((group) => group.layers.length > 0)
    : GEE_ALERT_GROUPS

  const hasOverlays = filteredOverlayGroups.some(
    (group) => group.overlays.length > 0
  )
  const hasGeeAlertLayers =
    filteredGeeGroups.some((group) => group.layers.length > 0)

  // LayerCard component is defined above the default export

  return (
    <>
      <div className={classes.root}>
        <Paper className={classes.tool}>
          <IconButton
            onClick={handleOpen}
            title={t('Layers')}
            className={`${classes.iconButton} ${open ? classes.active : ''}`}
            size="small"
          >
            <LayersIcon className={classes.icon} />
          </IconButton>
        </Paper>
      </div>

      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{ paper: classes.drawerPaper }}
        PaperProps={{ ref: paperRefCallback }}
      >
        <div className={classes.drawerHeader} style={noAppBar ? { marginTop: 0 } : undefined}>
          <div className={classes.headerRow}>
            <div className={classes.headerLeft}>
              <IconButton
                onClick={handleClose}
                size="small"
                className={classes.closeButton}
                style={{ backgroundColor: '#f0f1f3' }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {t('Layers')}
              </Typography>
            </div>
            {activeLayers.length > 0 && (
              <Button
                size="small"
                className={classes.clearAllButton}
                onClick={onClearAllLayers}
                startIcon={<ClearAllIcon style={{ fontSize: 16 }} />}
              >
                {t('Clear all')}
              </Button>
            )}
          </div>
          <TextField
            className={classes.searchField}
            placeholder={t('Search layers...')}
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: 'rgba(0, 0, 0, 0.3)' }} />
                </InputAdornment>
              ),
            }}
            style={{ marginTop: 12 }}
          />
          {activeLayers.length > 0 && (
            <div className={classes.activeChipsContainer}>
              {activeLayers.map((layer) => (
                <Chip
                  key={layer.name}
                  label={t(layer.name).length > 25 ? t(layer.name).substring(0, 25) + '...' : t(layer.name)}
                  size="small"
                  className={classes.activeChip}
                  onDelete={() => {
                    if (layer.type === 'overlay') handleOverlayToggle(layer.name)
                    else handleGeeAlertToggle(layer.name)
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <Divider style={{ opacity: 0.5 }} />
        <div ref={scrollContainerRef} className={classes.customScrollbar} style={{
          flex: 1,
          overflow: 'auto',
          width: '100%',
          maxWidth: DRAWER_WIDTH,
          boxSizing: 'border-box',
          padding: '8px 0 80px 0',
        }}>
          {/* Base Layers Section */}
          {filteredBaseLayers.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                {t('Base Layers')}
              </Typography>
              <List dense disablePadding style={{ padding: '0 8px' }}>
                {filteredBaseLayers.map((layer) => (
                  <div key={layer.name} className={classes.layerCard}>
                    <ListItem
                      button
                      onClick={() => handleBaseLayerSelect(layer.name)}
                      className={classes.layerItem}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        <Radio
                          checked={layer.checked}
                          size="small"
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(layer.name)}
                        className={layer.checked ? classes.listItemTextActive : classes.listItemText}
                      />
                    </ListItem>
                  </div>
                ))}
              </List>
            </>
          )}

          {filteredBaseLayers.length > 0 && hasOverlays && (
            <Divider style={{ margin: '12px 16px', opacity: 0.3 }} />
          )}

          {/* Overlays Section */}
          {hasOverlays && (
            <>
              <Typography className={classes.sectionTitle}>
                {t('Overlays')}
              </Typography>
              {filteredOverlayGroups.map((group) => {
                if (group.overlays.length === 0) return null
                const activeCount = group.overlays.filter((o) => o.visible).length
                const totalCount = group.overlays.length
                const accent = group.accent || DEFAULT_GEE_ACCENT
                return (
                  <Accordion
                    key={group.name}
                    defaultExpanded
                    className={classes.groupAccordion}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon style={{ fontSize: 18 }} />}
                      className={classes.groupAccordionSummary}
                    >
                      <Typography className={classes.groupTitle}>
                        {t(group.name)}
                      </Typography>
                      <span
                        className={classes.countBadge}
                        style={{
                          backgroundColor: activeCount > 0 ? accent : 'rgba(0, 0, 0, 0.06)',
                          color: activeCount > 0 ? '#fff' : 'rgba(0, 0, 0, 0.35)',
                        }}
                      >
                        {activeCount}/{totalCount}
                      </span>
                    </AccordionSummary>
                    <AccordionDetails className={classes.groupAccordionDetails}>
                      {group.overlays.map((overlay) => (
                        <LayerCard
                          key={overlay.name}
                          name={overlay.name}
                          visible={overlay.visible}
                          accent={accent}
                          legendUrl={overlay.legendUrl}
                          legendUrls={overlay.legendUrls}
                          legend={overlay.legend}
                          opacity={overlay.opacity}
                          noOpacity={overlay.noOpacity}
                          onOpacityChange={onOverlayOpacityChange}
                          onToggle={() => handleOverlayToggle(overlay.name)}
                          yearRange={overlay.yearRange}
                          legendGradient={overlay.legendGradient}
                          onYearRangeChange={onOverlayYearRangeChange}
                          years={overlay.years}
                          year={overlay.year}
                          onYearChange={onOverlayYearChange}
                          modes={overlay.modes}
                          mode={overlay.mode}
                          onModeChange={onOverlayModeChange}
                          classes={classes}
                          t={t}
                        />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </>
          )}

          {(filteredBaseLayers.length > 0 || hasOverlays) && hasGeeAlertLayers && (
            <Divider style={{ margin: '12px 16px', opacity: 0.3 }} />
          )}

          {/* Deforestation Alert Layers Section */}
          {hasGeeAlertLayers && (
            <>
              <Typography className={classes.sectionTitle}>
                {t('Deforestation Alert Layers')}
              </Typography>
              {filteredGeeGroups.map((group) => {
                if (group.layers.length === 0) return null
                const activeCount = group.layers.filter((layer) => geeAlertVisibility[layer.name]).length
                const totalCount = group.layers.length
                return (
                  <Accordion
                    key={group.name}
                    defaultExpanded
                    className={classes.groupAccordion}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon style={{ fontSize: 18 }} />}
                      className={classes.groupAccordionSummary}
                    >
                      <Typography className={classes.groupTitle}>
                        {t(group.name)}
                      </Typography>
                      <span
                        className={classes.countBadge}
                        style={{
                          backgroundColor: activeCount > 0 ? DEFAULT_GEE_ACCENT : 'rgba(0, 0, 0, 0.06)',
                          color: activeCount > 0 ? '#fff' : 'rgba(0, 0, 0, 0.35)',
                        }}
                      >
                        {activeCount}/{totalCount}
                      </span>
                    </AccordionSummary>
                    <AccordionDetails className={classes.groupAccordionDetails}>
                      {group.layers.map((layer) => {
                        const queryResult = geeAlertLayers[layer.name]
                        const isLoading = queryResult?.isLoading ?? false
                        const isError = queryResult?.isError ?? false
                        const isVisible = geeAlertVisibility[layer.name] ?? false
                        const legend = queryResult?.data?.legend
                        const opacity = geeAlertOpacity[layer.name] ?? 0.8

                        return (
                          <LayerCard
                            key={layer.name}
                            name={layer.name}
                            visible={isVisible}
                            accent={DEFAULT_GEE_ACCENT}
                            legend={legend}
                            isLoading={isLoading}
                            isError={isError}
                            opacity={opacity}
                            onOpacityChange={onGeeAlertOpacityChange}
                            onToggle={() => handleGeeAlertToggle(layer.name)}
                            {...(layer.windowDays && onGeeAlertWindowDaysChange ? {
                              windowDays: geeAlertWindowDays[layer.name] || DEFAULT_WINDOW_DAYS,
                              onWindowDaysChange: onGeeAlertWindowDaysChange,
                            } : {})}
                            classes={classes}
                            t={t}
                          />
                        )
                      })}
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </>
          )}
        </div>
      </Drawer>
    </>
  )
}
